import React, { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { parentDashboardApi } from "../../lib/api"
import ParentStatsBar from "./ParentStatsBar"
import StudentOverviewCard from "./StudentOverviewCard"
import StudentDetailPanel from "./StudentDetailPanel"
import AllStudentsPanel from "./AllStudentsPanel"

const ParentDashboard = () => {
  const [data, setData] = useState(null)
  const [selectedStudentId, setSelectedStudentId] = useState(null) // null = not yet set, "all" or a student ID
  const [selectedTermId, setSelectedTermId] = useState(null) // null means not yet set, will default to current
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const syncPollRef = useRef(null)

  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const lastFetchedTermRef = useRef(null)

  useEffect(() => {
    fetchDashboard()
  }, [])

  // Refetch when term changes (after initial load, only if user changed the term)
  useEffect(() => {
    if (initialLoadDone && selectedTermId !== lastFetchedTermRef.current) {
      fetchDashboard(selectedTermId)
    }
  }, [selectedTermId, initialLoadDone])

  const fetchDashboard = async (termId = null) => {
    try {
      setLoading(true)
      setError(null)

      // On initial load, use current_term_id by fetching without filter first
      // to discover what the current term is, then refetch with that term
      const response = await parentDashboardApi.get(termId)
      setData(response)

      // Default to current term if not yet set (only on initial load)
      if (!initialLoadDone && selectedTermId === null && response.current_term_id) {
        // Set the term but also track that we need to fetch with this term
        setSelectedTermId(response.current_term_id)
        lastFetchedTermRef.current = null // Force refetch with the new term
      } else {
        lastFetchedTermRef.current = termId
      }

      // Default student selection: if only one student, select them; otherwise show "all"
      if (selectedStudentId === null && response.students?.length > 0) {
        if (response.students.length === 1) {
          setSelectedStudentId(response.students[0].id)
        } else {
          setSelectedStudentId("all")
        }
      }

      setInitialLoadDone(true)
    } catch (err) {
      setError(err.message || "Failed to load dashboard")
    } finally {
      setLoading(false)
    }
  }

  const handleStudentSelect = (studentId) => {
    setSelectedStudentId(studentId)
  }

  const handleTermSelect = (termId) => {
    setSelectedTermId(termId)
  }

  const handleSync = async () => {
    if (syncing) return

    setSyncing(true)
    try {
      const response = await parentDashboardApi.requestSync(selectedStudentId)

      if (response.syncs_started > 0) {
        // Poll for completion - wait a bit then refresh
        syncPollRef.current = setTimeout(async () => {
          await fetchDashboard()
          setSyncing(false)
        }, 5000) // Give syncs time to complete, then refresh
      } else {
        setSyncing(false)
      }
    } catch (err) {
      console.error("Failed to start sync:", err)
      setSyncing(false)
    }
  }

  // Cleanup sync poll on unmount
  useEffect(() => {
    return () => {
      if (syncPollRef.current) {
        clearTimeout(syncPollRef.current)
      }
    }
  }, [])

  // Get currently selected student name for header
  const selectedStudent = selectedStudentId !== "all"
    ? data?.students?.find(s => s.id === selectedStudentId)
    : null

  // Get current term name
  const selectedTermName = selectedTermId
    ? data?.terms?.find(t => t.id === selectedTermId)?.name || "All Semesters"
    : "All Semesters"

  if (loading) {
    return (
      <div className="flex gap-6">
        {/* Sidebar skeleton */}
        <div className="hidden lg:block w-80 flex-shrink-0">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-[var(--cream)] rounded-xl w-2/3"></div>
            <div className="h-32 bg-[var(--cream)] rounded-2xl"></div>
            <div className="h-32 bg-[var(--cream)] rounded-2xl"></div>
          </div>
        </div>

        {/* Main content skeleton */}
        <div className="flex-1 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse h-24 bg-[var(--cream)] rounded-2xl"></div>
            ))}
          </div>
          <div className="animate-pulse h-96 bg-[var(--cream)] rounded-2xl"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-[var(--ink-muted)] mb-4">{error}</p>
          <button
            onClick={fetchDashboard}
            className="px-5 py-2.5 bg-[var(--ink)] text-white rounded-xl hover:bg-[var(--ink-muted)] transition-colors font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  const { students, aggregate_stats } = data || {}

  if (!students || students.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
            <svg className="w-10 h-10 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="font-display text-2xl font-semibold text-[var(--ink)] mb-3">No Students Linked</h3>
          <p className="text-[var(--ink-muted)] mb-8 leading-relaxed">
            You don't have any students linked to your account yet.
            Ask a student to send you an invitation from their settings.
          </p>
          <Link
            to="/settings"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--ink)] text-white rounded-xl hover:bg-[var(--ink-muted)] transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Go to Settings
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-6">
      {/* Student Sidebar */}
      <aside className="hidden lg:block w-80 flex-shrink-0">
        <div className="sticky top-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-[var(--ink)]">Your Students</h2>
            <span className="text-sm text-[var(--ink-light)] bg-[var(--cream)] px-2.5 py-1 rounded-lg font-medium">
              {students.length}
            </span>
          </div>

          {/* Term Selector */}
          {data?.terms?.length > 0 && (
            <div className="relative">
              <select
                value={selectedTermId ?? ""}
                onChange={(e) => handleTermSelect(e.target.value === "" ? null : parseInt(e.target.value, 10))}
                className="w-full appearance-none pl-3 pr-10 py-2.5 bg-[var(--cream)] border-0 rounded-xl text-sm font-medium text-[var(--ink)] hover:bg-[var(--paper-warm)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] cursor-pointer transition-colors"
              >
                <option value="">All Semesters</option>
                {data.terms.map((term) => (
                  <option key={term.id} value={term.id}>
                    {term.name}{term.id === data.current_term_id ? " (Current)" : ""}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-4 w-4 text-[var(--ink-light)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          )}

          {/* All Students option - only show if multiple students */}
          {students.length > 1 && (
            <button
              onClick={() => handleStudentSelect("all")}
              className={`
                w-full text-left p-4 rounded-2xl transition-all duration-200
                hover-lift group flex items-center gap-3
                ${selectedStudentId === "all"
                  ? "bg-[var(--ink)] text-white shadow-lg ring-2 ring-[var(--gold)]"
                  : "bg-white shadow-sm hover:shadow-md"
                }
              `}
            >
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center
                ${selectedStudentId === "all"
                  ? "bg-white/20"
                  : "bg-gradient-to-br from-[var(--ink)] to-[var(--ink-muted)]"
                }
              `}>
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`font-semibold ${selectedStudentId === "all" ? "text-white" : "text-[var(--ink)]"}`}>
                  All Students
                </h3>
                <p className={`text-sm ${selectedStudentId === "all" ? "text-white/70" : "text-[var(--ink-light)]"}`}>
                  Combined overview
                </p>
              </div>
            </button>
          )}

          <div className="space-y-3">
            {students.map((student, index) => (
              <div key={student.id} className="stagger-in" style={{ animationDelay: `${index * 50}ms` }}>
                <StudentOverviewCard
                  student={student}
                  isSelected={selectedStudentId === student.id}
                  onClick={() => handleStudentSelect(student.id)}
                />
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-xl sm:text-2xl font-semibold text-[var(--ink)]">
              {selectedStudentId === "all" ? "All Students" : selectedStudent?.name || "Parent Dashboard"}
            </h1>
            <p className="text-sm text-[var(--ink-light)] mt-0.5">{selectedTermName}</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Sync button */}
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--ink-muted)] bg-[var(--cream)] hover:bg-[var(--paper-warm)] rounded-xl transition-colors disabled:opacity-50"
              title="Sync data from Canvas"
            >
              {syncing ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="hidden sm:inline">Syncing...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="hidden sm:inline">Sync Now</span>
                </>
              )}
            </button>

            {/* Mobile selectors */}
            <div className="lg:hidden flex items-center gap-2">
            {/* Term selector (mobile) */}
            {data?.terms?.length > 0 && (
              <div className="relative">
                <select
                  value={selectedTermId ?? ""}
                  onChange={(e) => handleTermSelect(e.target.value === "" ? null : parseInt(e.target.value, 10))}
                  className="appearance-none pl-3 pr-8 py-2.5 bg-[var(--paper-warm)] border-0 rounded-xl text-sm font-medium text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] transition-shadow"
                >
                  <option value="">All</option>
                  {data.terms.map((term) => (
                    <option key={term.id} value={term.id}>
                      {term.name.length > 15 ? term.name.substring(0, 15) + "..." : term.name}
                    </option>
                  ))}
                </select>
                <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ink-muted)] pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            )}

            {/* Student selector (mobile) - only show if multiple students */}
            {students.length > 1 && (
              <div className="relative">
                <select
                  value={selectedStudentId}
                  onChange={(e) => handleStudentSelect(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))}
                  className="appearance-none pl-4 pr-10 py-2.5 bg-[var(--cream)] border-0 rounded-xl text-sm font-medium text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] transition-shadow"
                >
                  <option value="all">All Students</option>
                  {students.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name}
                    </option>
                  ))}
                </select>
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ink-muted)] pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Aggregate Stats */}
        <ParentStatsBar stats={aggregate_stats} />

        {/* Content Panel */}
        {selectedStudentId && (
          <div className="stagger-in">
            {selectedStudentId === "all" ? (
              <AllStudentsPanel students={students} termId={selectedTermId} />
            ) : (
              <StudentDetailPanel studentId={selectedStudentId} termId={selectedTermId} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ParentDashboard
